import React, { useMemo, useState } from 'react';
import Tooltip from './common/Tooltip';
import DonutChart from './DonutChart';
import { parseFloatFromString, formatCurrency, formatPercentage } from '../utils/formatters';

const formatSegmentTitle = (label, positions, value, percentage) =>
  `${label} (${positions} positions) - ${formatCurrency(value)} (${formatPercentage(percentage)})`;

const COLORS = [
  '#0EA5E9', // sky-500
  '#8B5CF6', // violet-500
  '#10B981', // emerald-500
  '#F97316', // orange-500
  '#EF4444', // red-500
  '#6366F1', // indigo-500
];

const MAX_VISIBLE = 5;

// Donut plus table of { name, value, positionCount } categories. Rows link only when onSelect is given.
const CategoryBreakdown = ({ title, categories = [], onSelect, animationKey }) => {
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    const processed = (categories || []).map((c, idx) => ({
      name: c.name,
      filterValue: c.filterValue,
      value: parseFloatFromString(c.value) || 0,
      positions: c.positionCount || 0,
      color: COLORS[idx % COLORS.length],
    })).filter((row) => row.value > 0);

    processed.sort((a, b) => b.value - a.value);

    const total = processed.reduce((sum, row) => sum + row.value, 0);
    return processed.map((row) => ({
      ...row,
      percentage: total > 0 ? (row.value / total) * 100 : 0,
    }));
  }, [categories]);

  const totalValue = useMemo(
    () => rows.reduce((sum, row) => sum + row.value, 0),
    [rows]
  );

  const chartData = useMemo(() => rows.map((row) => ({
    name: row.name,
    filterValue: row.filterValue,
    value: row.value,
    color: row.color,
    tooltip: formatSegmentTitle(row.name, row.positions, row.value, row.percentage),
  })), [rows]);

  if (rows.length === 0) return null;

  const visibleRows = expanded ? rows : rows.slice(0, MAX_VISIBLE);
  const hiddenCount = rows.length - MAX_VISIBLE;

  return (
    <section aria-label={title} className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{title}</h3>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative flex-shrink-0 flex items-center justify-center mx-auto sm:mx-0" style={{ width: 140, height: 140 }}>
          <DonutChart
            data={chartData.slice(0, MAX_VISIBLE)}
            animationKey={animationKey}
            size={140}
            thickness={26}
            totalLabel=""
            showLabels={false}
            showLegend={false}
            onSegmentClick={onSelect ? (segment) => onSelect(segment.name, segment.filterValue) : undefined}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xs text-gray-500 dark:text-gray-400">Value</span>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(totalValue)}
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 dark:text-gray-500 text-[10px]">
                <th className="text-left font-normal pb-1"></th>
                <th className="text-right font-normal pb-1">%</th>
                <th className="text-right font-normal pb-1">Value</th>
                <th className="text-right font-normal pb-1">Positions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, idx) => (
                <tr
                  key={row.filterValue ?? row.name}
                  className={idx > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}
                >
                  <td className="py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: row.color }} />
                      <Tooltip title={formatSegmentTitle(row.name, row.positions, row.value, row.percentage)} position="top" arrow={true} theme="dark" size="small">
                        {onSelect ? (
                          <button
                            onClick={() => onSelect(row.name, row.filterValue)}
                            className="text-gray-800 dark:text-gray-200 hover:underline text-left truncate"
                          >
                            {row.name}
                          </button>
                        ) : (
                          <span className="text-gray-800 dark:text-gray-200 truncate">{row.name}</span>
                        )}
                      </Tooltip>
                    </div>
                  </td>
                  <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">
                    {formatPercentage(row.percentage)}
                  </td>
                  <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                    {formatCurrency(row.value)}
                  </td>
                  <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                    {row.positions}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hiddenCount > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more...`}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CategoryBreakdown;
